/**
 * 배포 감지 → 다음 라우트 이동을 풀 로드로 전환해 새 버전 적용
 * - 빌드 시 번들에 박힌 __BUILD_VERSION__과 서버의 /version.json을 비교한다.
 * - 체크는 라우트 이동 시점에만, 최소 60초 간격으로 수행한다(요청 스팸 방지).
 * - 새 버전 감지 시 SPA 전환 대신 location.assign으로 목적지를 풀 로드한다.
 *   → 보고 있는 화면이 갑자기 리로드되는 일은 없고, 배포 후 첫 이동 한 번만 풀 로드가 된다.
 * - 탭 복귀(visibilitychange) 시 백그라운드로 미리 체크해 두므로,
 *   다음 이동에서는 fetch 대기 없이 즉시 전환된다.
 * - version.json과 번들이 어긋난 경우(프록시 캐시 등)를 대비해 같은 서버 버전으로는
 *   리로드를 1회만 시도한다(무한 리로드 방지).
 * - 풀 로드 URL에는 1회용 캐시 버스터 쿼리를 붙여, 서버가 index.html에 Cache-Control을
 *   주지 않아 휴리스틱 캐시가 생겨도 항상 새 문서를 받는다(부팅 직후 주소에서 제거).
 */
const CHECK_INTERVAL_MS = 60 * 1000
const FETCH_TIMEOUT_MS = 3000
const RELOAD_GUARD_KEY = '__version_reload_for'
// 풀 로드 URL에 붙는 캐시 버스터. 제거는 index.html 인라인 스크립트가 담당한다
// (라우터가 생성 시점에 URL을 스냅샷으로 뜨므로 모듈 코드에서는 제거가 불가능).
const CACHE_BUSTER_PARAM = '_v'

// vite.config.js의 define으로 빌드 시 주입된다 (vite serve에서는 체크 자체를 비활성)
const BUILD_VERSION = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : null

let detectedVersion = null // 감지된 새 서버 버전 (한 번 감지되면 유지)
let lastCheckedAt = 0

async function fetchServerVersion() {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

  try {
    const res = await fetch(`${import.meta.env.BASE_URL}version.json?_=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
    if (!res.ok) return null

    // version.json이 아직 배포되지 않은 서버는 SPA fallback으로 HTML(200)을
    // 돌려주므로 json 파싱이 실패한다 → catch에서 null 처리
    const data = await res.json()
    return typeof data?.version === 'string' ? data.version : null
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

/** 새 버전 배포 여부 확인 (60초 쓰로틀, 감지 후에는 fetch 없이 즉시 true) */
async function checkForNewVersion() {
  if (detectedVersion) return true

  const now = Date.now()
  if (now - lastCheckedAt < CHECK_INTERVAL_MS) return false
  lastCheckedAt = now

  const serverVersion = await fetchServerVersion()
  if (!serverVersion || serverVersion === BUILD_VERSION) return false

  // 이 서버 버전으로 이미 리로드했는데도 번들이 그대로면(캐시 어긋남) 반복 리로드하지 않는다
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === serverVersion) return false

  detectedVersion = serverVersion
  return true
}

export function setupVersionCheck(router) {
  // 로컬 vite serve에는 version.json이 없으므로 비활성
  if (!BUILD_VERSION || import.meta.env.DEV) return () => {}

  // 직전 리로드로 새 버전이 정상 적용됐으면 가드 키를 비워 다음 배포를 다시 감지한다
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === BUILD_VERSION) {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
  }

  const removeGuard = router.beforeEach(async (to, from) => {
    // 첫 진입은 방금 받은 최신 번들이므로 체크하지 않는다 (리로드 루프 방지)
    if (!from.matched.length) return true
    if (!(await checkForNewVersion())) return true

    sessionStorage.setItem(RELOAD_GUARD_KEY, detectedVersion)
    console.warn(`[VersionCheck] 새 배포 감지(${detectedVersion}) → 풀 로드 전환: ${to.fullPath}`)

    // 캐시에 없는 URL로 요청해 휴리스틱 캐시된 구버전 index.html을 우회한다
    const target = router.resolve(to.fullPath).href
    const separator = target.includes('?') ? '&' : '?'
    window.location.assign(`${target}${separator}${CACHE_BUSTER_PARAM}=${Date.now()}`)

    return false // SPA 전환 취소 (location.assign이 이어받는다)
  })

  // 탭 복귀 시 백그라운드 선체크 → 다음 이동에서 대기 없이 바로 전환
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') checkForNewVersion()
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)

  return () => {
    removeGuard()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }
}
